import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './pages/login-page';
import { DashboardPage } from './pages/dashboard-page';
import { VendorListPage } from './pages/vendors/vendor-list-page';
import { VendorDetailPage } from './pages/vendors/vendor-detail-page';
import { UserListPage } from './pages/users/user-list-page';
import { UserDetailPage } from './pages/users/user-detail-page';
import { PetDetailPage, PetDetailRedirect } from './pages/users/pet-detail-page';
import { BookingListPage } from './pages/bookings/booking-list-page';
import { BookingDetailPage } from './pages/bookings/booking-detail-page';
import { ReviewListPage } from './pages/reviews/review-list-page';
import { OrderListPage } from './pages/orders/order-list-page';
import { CommerceOrderListPage } from './pages/commerce-orders/commerce-order-list-page';
import { CommerceOrderDetailPage } from './pages/commerce-orders/commerce-order-detail-page';
import { SupportRequestListPage } from './pages/support-requests/support-request-list-page';
import { SupportRequestDetailPage } from './pages/support-requests/support-request-detail-page';
import { AdoptionListPage } from './pages/adoption/adoption-list-page';
import { AccountDeletionListPage } from './pages/account-deletions/account-deletion-list-page';
import { BannerListPage } from './pages/banners/banner-list-page';
import { AgreementsPage } from './pages/agreements/agreements-page';
import { AdoptionDetailPage } from './pages/adoption/adoption-detail-page';
import { AdminListPage } from './pages/admins/admin-list-page';
import { ServiceRegionPage } from './pages/service-regions/service-region-page';
import { PaymentListPage } from './pages/payments/payment-list-page';
import { PaymentDetailPage } from './pages/payments/payment-detail-page';
import { ReportPage } from './pages/reports/report-page';
import { ProductApprovalPage } from './pages/product-approval/product-approval-page';
import { ProductReviewPage } from './pages/product-approval/product-review-page';
import { CommissionPage } from './pages/commission/commission-page';
import { CatalogProductPage } from './pages/catalog-products/catalog-product-page';
import { ProductCategoryPage } from './pages/product-categories/product-category-page';
import { ModulePage } from '@/pages/modules/module-page';
import { AppConfigPage } from '@/pages/app-config/app-config-page';
import { CatalogProductDetailPage } from './pages/catalog-products/catalog-product-detail-page';
import { use_auth_store, use_is_super_admin } from './lib/auth-store';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = use_auth_store((s) => s.access_token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Super-admin-only route. Sends a normal admin to the dashboard rather than the
 * login screen — they are signed in, this simply isn't theirs. The backend
 * refuses the endpoints regardless; this only keeps the console honest.
 */
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const token = use_auth_store((s) => s.access_token);
  const is_super = use_is_super_admin();
  if (!token) return <Navigate to="/login" replace />;
  if (!is_super) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vendors"
        element={
          <RequireAuth>
            <VendorListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <RequireAuth>
            <VendorDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth>
            <UserListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users/:id"
        element={
          <RequireAuth>
            <UserDetailPage />
          </RequireAuth>
        }
      />
      {/*
        Pets live inside their owner's file — see pages/users/pet-detail-page.
        A pet only exists as somebody's pet, so the canonical address is
        owner-scoped and there is no top-level Pets module any more.
      */}
      <Route
        path="/users/:user_id/pets/:pet_id"
        element={
          <RequireAuth>
            <PetDetailPage />
          </RequireAuth>
        }
      />
      {/* Old top-level URLs: resolve the owner and forward, so bookmarks and
          links pasted into tickets keep working. */}
      <Route
        path="/pets/:id"
        element={
          <RequireAuth>
            <PetDetailRedirect />
          </RequireAuth>
        }
      />
      <Route path="/pets" element={<Navigate to="/users" replace />} />
      <Route
        path="/bookings"
        element={
          <RequireAuth>
            <BookingListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/bookings/:id"
        element={
          <RequireAuth>
            <BookingDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reviews"
        element={
          <RequireAuth>
            <ReviewListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireAuth>
            <OrderListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/commerce-orders"
        element={
          <RequireAuth>
            <CommerceOrderListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/commerce-orders/:id"
        element={
          <RequireAuth>
            <CommerceOrderDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/support-requests"
        element={
          <RequireAuth>
            <SupportRequestListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/support-requests/:id"
        element={
          <RequireAuth>
            <SupportRequestDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/account-deletions"
        element={
          <RequireAuth>
            <AccountDeletionListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/adoption"
        element={
          <RequireAuth>
            <AdoptionListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/adoption/:id"
        element={
          <RequireAuth>
            <AdoptionDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/banners"
        element={
          <RequireAuth>
            <BannerListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agreements"
        element={
          <RequireAuth>
            <AgreementsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admins"
        element={
          <RequireSuperAdmin>
            <AdminListPage />
          </RequireSuperAdmin>
        }
      />
      <Route
        path="/service-regions"
        element={
          <RequireAuth>
            <ServiceRegionPage />
          </RequireAuth>
        }
      />
      <Route
        path="/payments"
        element={
          <RequireAuth>
            <PaymentListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/payments/:id"
        element={
          <RequireAuth>
            <PaymentDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth>
            <ReportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/product-approvals"
        element={
          <RequireAuth>
            <ProductApprovalPage />
          </RequireAuth>
        }
      />
      <Route
        path="/product-approvals/:vertical/:id"
        element={
          <RequireAuth>
            <ProductReviewPage />
          </RequireAuth>
        }
      />
      <Route
        path="/catalogue"
        element={
          <RequireAuth>
            <CatalogProductPage />
          </RequireAuth>
        }
      />
      <Route
        path="/catalogue/:id"
        element={
          <RequireAuth>
            <CatalogProductDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/product-categories"
        element={
          <RequireAuth>
            <ProductCategoryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/modules"
        element={
          <RequireAuth>
            <ModulePage />
          </RequireAuth>
        }
      />
      <Route
        path="/platform-fees"
        element={
          <RequireAuth>
            <CommissionPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app-config"
        element={
          <RequireAuth>
            <AppConfigPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
